// RODAPÉ "Atelier" (rodape = 9): rodapé editorial do modelo "Atelier" (papel, tinta, linhas finas,
// acento do tenant).
//  - FAIXA DE CONVITE: frase em serifa ("Vamos encontrar o seu próximo endereço?") + botões
//    WhatsApp (ou Ligar, sem WhatsApp) / Ligar / E-mail (e-mail revelado no clique, como os
//    rodapés existentes);
//  - COLUNAS reveladas em cascata (whileInView, once): Endereço (+ filiais via FiliaisRodape) ·
//    Contato (telefones, e-mail) · Navegação (Início, transações, menus do tenant, blog —
//    mesma origem do rodapé Horizonte) · Redes sociais; "Buscas populares" (cidades e tipos do
//    tenant, mesmas URLs de listagem dos rodapés atuais) quando !hidePopular — e o bloco
//    "Pesquisas mais populares" (Popular) segue sendo renderizado como nos demais rodapés;
//  - FECHO: o NOME DA IMOBILIÁRIA GIGANTE em serifa ocupando a largura (tamanho em cqw pelo nº
//    de letras, com clamp de reserva), revelado por máscara ao entrar na tela; abaixo copyright,
//    CRECI/CNPJ, política de privacidade, termos, html_rodape e selo ImobTotal (gate dentro do
//    componente); botão "voltar ao topo" à ESQUERDA da linha final (longe do WhatsApp flutuante,
//    que mora no canto inferior direito).
// prefers-reduced-motion: sem cascata nem máscara. Default export = ErrorBoundary com fallback.
import { motion, MotionConfig } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FaFacebook, FaInstagram, FaLinkedin, FaPhoneAlt, FaTiktok, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { MaskPhone, removerAcentos } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { trackLead } from '../../whatsAppButton/WhatsAppButton';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import type { RodapeSectionProps } from '../../registry/types';
import FiliaisRodape from '../filiais-rodape/FiliaisRodape';
import Popular from '../populares/Popular';
import LinhaSeoRodape from '../rodape-seo/LinhaSeoRodape';
import { SeloImobtotalItem } from '../selo-imobtotal/SeloImobtotal';
import { contatosDoTenant, EASE_ATELIER, linkWhatsAppDoTenant, logoAtelier, nomeDaEmpresa, nomeSemSufixoJuridico, redesDoTenant, urlDaListagem, useMovimentoReduzido, varsAtelier } from '../atelier/atelier';
import type { ItemMenu } from '../atelier/atelier';
import styles from './AtelierFooter.module.css';

// Páginas do tenant que são "institucionais" (ficam depois das transações na navegação).
const INSTITUCIONAL_RE = /quem somos|sobre n|a empresa|nossa historia|institucional|trabalhe|privacidade|lgpd|termos/;
const ehInstitucional = (titulo: unknown): boolean => INSTITUCIONAL_RE.test(removerAcentos(String(titulo ?? '')).toLowerCase().replace(/\s+/g, ' '));

const ICONE_REDE: Record<string, React.ComponentType<{ 'aria-hidden'?: boolean }>> = {
    Instagram: FaInstagram,
    Facebook: FaFacebook,
    Youtube: FaYoutube,
    Tiktok: FaTiktok,
    Linkedin: FaLinkedin,
};

const FRASE_CONVITE = 'Vamos encontrar o seu próximo endereço?';

const IconeSeta = ({ cima = false }: { cima?: boolean }) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {cima ? (
            <>
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="6 11 12 5 18 11" />
            </>
        ) : (
            <>
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
            </>
        )}
    </svg>
);
const IconeEmail = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="1.5" />
        <polyline points="3 7 12 13 21 7" />
    </svg>
);

const AtelierFooterInner: React.FC<RodapeSectionProps> = ({ site: siteProp, origin, codsite, allRodape, hidePopular, allCities, allCitiesTipes }) => {
    // MULTI-REGIÃO: contatos/endereço trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const codsiteQuery = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const reduzido = useMovimentoReduzido();
    const [isMounted, setIsMounted] = useState(false);
    const [emailVisivel, setEmailVisivel] = useState(false);
    useEffect(() => setIsMounted(true), []);
    const withCodsite = (href: string) => (isMounted ? appendCodsiteQuery(href, codsite ?? codsiteQuery, origin) : href);
    const trackFooter = (nome: string, cat: string) => () => trackMetaEvent(site, 'ViewContent', { content_name: nome, content_category: cat });

    // ---- navegação: Início + transações + menus do tenant + blog (mesma origem do Horizonte) ----
    const menusDisponiveis = (allDataHome?.menus || []).filter((m: any) => m?.Disponivel && m?.titulo);
    const paraItem = (m: any): ItemMenu => ({
        label: m.titulo,
        href: m?.link ? m.link : `/menu/${m.titulo}`,
        external: Boolean(m?.link && !String(m.link).startsWith('/')),
    });
    const transacaoPrincipal = String(site?.transacoes?.[0] || 'venda').toLowerCase();
    const navegacao: ItemMenu[] = [{ label: 'Início', href: '/' }];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        navegacao.push({ label: `Imóveis para ${low === 'aluguel' ? 'alugar' : low === 'venda' ? 'comprar' : low}`, href: `/imoveis/${low}/-/-/-/-` });
    });
    menusDisponiveis
        .filter((m: any) => !ehInstitucional(m.titulo))
        .slice(0, 7)
        .forEach((m: any) => navegacao.push(paraItem(m)));
    menusDisponiveis
        .filter((m: any) => ehInstitucional(m.titulo) && !/privacidade|lgpd|termos/i.test(String(m.titulo)))
        .slice(0, 4)
        .forEach((m: any) => navegacao.push(paraItem(m)));
    if (allDataHome?.blogs?.[0]?.id) navegacao.push({ label: 'Blog', href: '/blog' });

    const legais: ItemMenu[] = menusDisponiveis.filter((m: any) => /privacidade|lgpd|termos/i.test(String(m.titulo))).map(paraItem);
    if (!legais.some((i) => /privacidade|lgpd/i.test(i.label))) legais.push({ label: 'Política de privacidade', href: '/politica' });
    if (!legais.some((i) => /termos/i.test(i.label))) legais.push({ label: 'Termos de uso', href: '/termos' });

    // ---- contato ----
    const { phones, ctaPhone, ctaIsWhats, ctaHref } = contatosDoTenant(site, 4);
    const telefoneLigar = phones.find((p) => !p.whats) || (ctaIsWhats ? phones.find((p) => p !== ctaPhone) : undefined);
    const email = site?.Dados?.email1 || site?.Dados?.email2;
    const enderecoLinhas = [site?.Dados?.endereco, [site?.Dados?.bairro, site?.Dados?.cidade].filter(Boolean).join(' - '), site?.Dados?.cep ? `CEP ${site.Dados.cep}` : ''].filter(Boolean) as string[];
    const redes = redesDoTenant(site);
    // rodapé no papel: a arte própria para fundo branco (a do cadastro costuma ser clara e sumir aqui)
    const logo = logoAtelier(site, 'branco');
    const logoSrc = logo.src;
    const empresa = nomeDaEmpresa(site);
    const creci = site?.Dados?.creci;
    const cnpj = site?.Dados?.cnpj;

    // ---- buscas populares: cidades (com imóvel, quando a contagem vier) e tipos do tenant ----
    const populares = useMemo(() => {
        const todasCidades = (Array.isArray(allCities) ? allCities : []).filter((c: any) => c?.label);
        const algumaComContagem = todasCidades.some((c: any) => Number(c?.imoveis) > 0);
        const cidades: ItemMenu[] = todasCidades
            .filter((c: any) => !algumaComContagem || Number(c?.imoveis) > 0)
            .slice(0, 6)
            .map((c: any) => ({ label: `Imóveis em ${String(c.label).trim()}`, href: urlDaListagem(transacaoPrincipal, String(c.label).trim(), undefined, site?.id) }));
        const tipos: ItemMenu[] = (Array.isArray(allCitiesTipes) ? allCitiesTipes : [])
            .map((t: any) => String(t?.nome || t?.label || '').trim())
            .filter(Boolean)
            .slice(0, 6)
            .map((nome: string) => ({ label: `${nome}${transacaoPrincipal === 'aluguel' ? ' para alugar' : ' à venda'}`, href: urlDaListagem(transacaoPrincipal, '', nome, site?.id) }));
        return cidades.concat(tipos);
    }, [allCities, allCitiesTipes, transacaoPrincipal]);

    // ---- revelação das colunas ----
    const revela = (i: number) => ({
        initial: reduzido ? false : ({ opacity: 0, y: 28 } as const),
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.2 },
        transition: { duration: 0.8, delay: reduzido ? 0 : i * 0.08, ease: EASE_ATELIER },
    });
    const voltarAoTopo = () => {
        trackMetaEvent(site, 'ViewContent', { content_name: 'Voltar ao topo', content_category: 'footer_navigation' });
        window.scrollTo({ top: 0, behavior: reduzido ? 'auto' : 'smooth' });
    };

    // nome gigante sem o sufixo jurídico (Ltda/Eireli/ME…); o copyright, abaixo, mantém o nome completo
    const nomeFecho = nomeSemSufixoJuridico((empresa || site?.Dados?.empresa || '').toString());
    const varsRodape = {
        ...varsAtelier(site),
        '--at-letras': Math.max(4, nomeFecho.length),
        // celular: a maior palavra dita o tamanho e o nome quebra por palavra
        '--at-palavra': Math.max(3, ...nomeFecho.split(/\s+/).map((p) => p.length)),
    } as CSSProperties;

    // desktop: o CSS só ESTIMA o tamanho pelo nº de letras (nome de letras estreitas ficava em ~72%
    // da largura; caixa-alta estouraria e sairia cortado). Aqui o nome é MEDIDO depois das fontes
    // carregarem e ajustado para ocupar a largura. Celular (quebra por palavra) fica com o CSS.
    const nomeRef = useRef<HTMLSpanElement>(null);
    useEffect(() => {
        const el = nomeRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        let vivo = true;
        let ultimaLargura = -1;
        const ajustar = () => {
            const largura = el.clientWidth;
            if (!vivo || largura === ultimaLargura) return;
            ultimaLargura = largura;
            el.style.fontSize = '';
            if (!largura || getComputedStyle(el).whiteSpace !== 'nowrap') return;
            const faixa = document.createRange();
            faixa.selectNodeContents(el);
            const texto = faixa.getBoundingClientRect().width;
            if (!texto) return;
            const atual = parseFloat(getComputedStyle(el).fontSize);
            const teto = Math.min(window.innerHeight * 0.42, 260);
            el.style.fontSize = `${Math.min((atual * largura * 0.985) / texto, teto)}px`;
        };
        const observador = new ResizeObserver(ajustar);
        observador.observe(el);
        document.fonts?.ready.then(() => {
            ultimaLargura = -1;
            ajustar();
        });
        return () => {
            vivo = false;
            observador.disconnect();
        };
    }, [nomeFecho]);

    const renderLink = (item: ItemMenu, idx: number, cat: string) =>
        item.external ? (
            <li key={idx}>
                <a href={item.href} className={styles.link} rel="noopener noreferrer" onClick={trackFooter(item.label, cat)}>
                    {item.label}
                </a>
            </li>
        ) : (
            <li key={idx}>
                <Link prefetch={false} href={withCodsite(item.href)} className={styles.link} onClick={trackFooter(item.label, cat)}>
                    {item.label}
                </Link>
            </li>
        );

    const ctaContato = (metodo: 'whatsapp' | 'phone') => () => {
        trackLead(site, null); // whatsapp_click no GTM/GA, como no rodapé clássico (sem imóvel = sem Meta)
        trackMetaEvent(site, 'Contact', { content_name: 'Convite do rodapé', contact_method: metodo, content_category: 'footer_contact' });
    };

    return (
        <MotionConfig reducedMotion="user">
            {!hidePopular && <Popular site={site} allRodape={allRodape} codsite={codsite} origin={origin} />}
            {/* BUILDER: âncora estável do bloco "rodape". */}
            <footer className={`${styles.footer} ${reduzido ? styles.semMovimento : ''}`} data-builder-section="rodape" style={varsRodape}>
                {/* ---------- convite ---------- */}
                <div className={styles.convite}>
                    <motion.h2 className={styles.conviteTitulo} {...revela(0)}>
                        {FRASE_CONVITE}
                    </motion.h2>
                    <motion.div className={styles.conviteAcoes} {...revela(1)}>
                        {ctaPhone ? (
                            <a className={styles.botaoPrincipal} href={ctaHref} rel="noopener noreferrer" target={ctaIsWhats ? '_blank' : undefined} title={ctaPhone.nome || (ctaIsWhats ? 'WhatsApp' : 'Telefone')} onClick={ctaContato(ctaIsWhats ? 'whatsapp' : 'phone')}>
                                {ctaIsWhats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                {ctaIsWhats ? 'WhatsApp' : `Ligar ${MaskPhone(ctaPhone.tel || '')}`}
                            </a>
                        ) : null}
                        {ctaIsWhats && telefoneLigar?.tel ? (
                            <a className={styles.botao} href={`tel:+55${(telefoneLigar.tel || '').replace(/\D/g, '')}`} title={telefoneLigar.nome || 'Telefone'} onClick={ctaContato('phone')}>
                                <FaPhoneAlt aria-hidden="true" />
                                Ligar
                            </a>
                        ) : null}
                        {email ? (
                            emailVisivel ? (
                                <a className={styles.botao} href={`mailto:${email}`}>
                                    <IconeEmail />
                                    {email}
                                </a>
                            ) : (
                                <button type="button" className={styles.botao} onClick={() => setEmailVisivel(true)} aria-label="Mostrar o e-mail da imobiliária">
                                    <IconeEmail />
                                    E-mail
                                </button>
                            )
                        ) : null}
                    </motion.div>
                </div>

                {/* ---------- colunas ---------- */}
                <div className={styles.conteudo}>
                    <div className={`${styles.grade} ${populares.length > 0 && !hidePopular ? styles.gradeCinco : ''}`}>
                        <motion.div className={`${styles.col} ${styles.colMarca}`} {...revela(0)}>
                            {logoSrc ? (
                                <Link prefetch={false} href={withCodsite('/')} className={styles.logoLink} title={`Imobiliária ${empresa}`}>
                                    <Image src={logoSrc} alt={`Imobiliária ${empresa}`} className={logo.empilhada ? `${styles.logo} ${styles.logoEmpilhada}` : styles.logo} width={200} height={72} unoptimized />
                                </Link>
                            ) : null}
                            <span className={styles.colTitulo}>Endereço</span>
                            {enderecoLinhas.length ? (
                                <address className={styles.endereco}>
                                    {enderecoLinhas.map((l, i) => (
                                        <span key={i}>{l}</span>
                                    ))}
                                </address>
                            ) : (
                                <span className={styles.endereco}>{empresa}</span>
                            )}
                            {creci ? <span className={styles.creci}>CRECI {creci}</span> : null}
                        </motion.div>

                        <motion.div className={`${styles.col} ${styles.colContato}`} {...revela(1)}>
                            <span className={styles.colTitulo}>Contato</span>
                            <ul className={styles.lista}>
                                {phones.map((p, idx) => {
                                    const href = p.whats ? linkWhatsAppDoTenant(site, p.tel) : `tel:+55${(p.tel || '').replace(/\D/g, '')}`;
                                    return (
                                        <li key={idx}>
                                            <a className={`${styles.link} ${styles.contato}`} href={href} rel="noopener noreferrer" target={p.whats ? '_blank' : undefined} onClick={() => { trackLead(site, null); trackFooter('Telefone do rodapé', 'footer_contact')(); }}>
                                                {p.whats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                                <span>
                                                    {MaskPhone(p.tel || '')}
                                                    {p.nome ? ` — ${p.nome}` : ''}
                                                </span>
                                            </a>
                                        </li>
                                    );
                                })}
                                {email ? (
                                    <li>
                                        {emailVisivel ? (
                                            <a className={styles.link} href={`mailto:${email}`}>
                                                {email}
                                            </a>
                                        ) : (
                                            <button type="button" className={`${styles.link} ${styles.verEmail}`} onClick={() => setEmailVisivel(true)}>
                                                Ver e-mail
                                            </button>
                                        )}
                                    </li>
                                ) : null}
                            </ul>
                        </motion.div>

                        <motion.div className={styles.col} {...revela(2)}>
                            <span className={styles.colTitulo}>Navegação</span>
                            <ul className={styles.lista}>{navegacao.map((item, idx) => renderLink(item, idx, 'footer_navigation'))}</ul>
                        </motion.div>

                        {populares.length > 0 && !hidePopular ? (
                            <motion.div className={styles.col} {...revela(3)}>
                                <span className={styles.colTitulo}>Buscas populares</span>
                                <ul className={styles.lista}>{populares.map((item, idx) => renderLink(item, idx, idx < 6 ? 'footer_city' : 'footer_type'))}</ul>
                            </motion.div>
                        ) : null}

                        <motion.div className={styles.col} {...revela(4)}>
                            <span className={styles.colTitulo}>Redes sociais</span>
                            {redes.length > 0 ? (
                                <ul className={styles.lista}>
                                    {redes.map((r) => {
                                        const Icone = ICONE_REDE[r.rede];
                                        return (
                                            <li key={r.rede}>
                                                <a href={r.href} target="_blank" rel="noopener noreferrer" className={`${styles.link} ${styles.contato}`} title={`Navegar para o ${r.rede} da imobiliária`}>
                                                    {Icone ? <Icone aria-hidden={true} /> : null}
                                                    <span>{r.rede}</span>
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <span className={styles.semRedes}>Fale conosco pelos nossos telefones.</span>
                            )}
                        </motion.div>
                    </div>

                    {/* MULTI-FILIAIS: endereços de TODAS as filiais (null fora do modo popup). */}
                    <FiliaisRodape site={site} fundo="#f5f1ea" cor="#161514" variante="atelier" />

                    {/* SEO: o que o rodapé clássico lista e "Buscas populares" não tem (empreendimentos e
                        cidades/tipos além do limite dela), em linha discreta (16/10) */}
                    <LinhaSeoRodape
                        site={site}
                        allCities={allCities}
                        allCitiesTipes={allCitiesTipes}
                        allRodape={allRodape}
                        withCodsite={withCodsite}
                        jaTem={[...(populares.length > 0 && !hidePopular ? populares : []), ...navegacao, ...legais].map((i) => i.href)}
                    />
                </div>

                {/* ---------- fecho: nome gigante ---------- */}
                {/* quem é OBSERVADO é o contêiner (sem clip-path): o Chrome aplica o clip-path do
                    próprio alvo ao calcular a interseção, e com inset(100%) a área é zero — o
                    whileInView nunca dispararia no span mascarado. As variantes propagam ao filho. */}
                {nomeFecho ? (
                    <motion.div className={styles.fecho} aria-hidden="true" initial={reduzido ? false : 'oculto'} whileInView="visivel" viewport={{ once: true, amount: 0.4 }}>
                        <motion.span
                            ref={nomeRef}
                            className={styles.fechoNome}
                            variants={{ oculto: { clipPath: 'inset(100% 0 0 0)', y: 24 }, visivel: { clipPath: 'inset(0% 0 0 0)', y: 0 } }}
                            transition={{ duration: reduzido ? 0 : 1.1, ease: EASE_ATELIER }}
                        >
                            {nomeFecho}
                        </motion.span>
                    </motion.div>
                ) : null}

                {/* ---------- linha final ---------- */}
                <div className={styles.linhaFinal}>
                    <button type="button" className={styles.voltarTopo} onClick={voltarAoTopo} aria-label="Voltar ao topo da página" title="Voltar ao topo">
                        <IconeSeta cima />
                        <span>Topo</span>
                    </button>
                    <div className={styles.linhaFinalTexto}>
                        <span className={styles.legal}>
                            © {new Date().getFullYear()} {empresa}. Todos os direitos reservados.
                            {creci ? ` · CRECI ${creci}` : ''}
                            {cnpj ? ` · CNPJ ${cnpj}` : ''}
                        </span>
                        <ul className={styles.legais}>{legais.map((item, idx) => renderLink(item, idx, 'footer_institutional'))}</ul>
                        {site?.html_rodape ? <span className={styles.htmlRodape} dangerouslySetInnerHTML={{ __html: site?.html_rodape }} /> : null}
                        {/* Selo ImobTotal: respeita copyright + EsconderRodapeSobressai (gate dentro do componente). */}
                        <SeloImobtotalItem site={site} host={origin} alinhar="esquerda" linhaPropria fonte="13px" />
                    </div>
                </div>
            </footer>
        </MotionConfig>
    );
};

class AtelierFooter extends React.Component<RodapeSectionProps, { hasError: boolean }> {
    constructor(props: RodapeSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro rodapé padrão sem quebrar a página.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <AtelierFooterInner {...this.props} />;
    }
}

export default AtelierFooter;
